import React, { useEffect, useState } from 'react';
import { cn } from '../../lib/cn';
import { EmptyState } from '../EmptyState/EmptyState';
import { Modal } from '../Modal/Modal';

export interface TriageSourceImageView {
  key: string;
  label: string;
  url: string | null;
  loading?: boolean;
  error?: boolean;
}

export interface TriageSourceImageModalProps {
  open: boolean;
  onClose: () => void;
  title: React.ReactNode;
  images: TriageSourceImageView[];
  alt?: string;
}

export function TriageSourceImageModal({
  open,
  onClose,
  title,
  images,
  alt = '',
}: TriageSourceImageModalProps) {
  const [activeKey, setActiveKey] = useState<string | null>(null);

  useEffect(() => {
    if (open) setActiveKey(null);
  }, [open]);

  const active = images.find((i) => i.key === activeKey) ?? images[0];

  let body: React.ReactNode;
  if (!active) {
    body = <EmptyState variant="box" size="sm" icon="image" title="No image was uploaded." />;
  } else if (active.loading) {
    body = <div className="py-10 text-center text-[var(--ink-3)]">Loading…</div>;
  } else if (active.error) {
    body = <div className="py-10 text-center text-[var(--terracotta)]">Couldn&apos;t load the image.</div>;
  } else if (!active.url) {
    body = <EmptyState variant="box" size="sm" icon="image" title="No image was uploaded." />;
  } else {
    body = (
      <img
        src={active.url}
        alt={images.length > 1 ? `${alt} — ${active.label}` : alt}
        className="mx-auto block max-h-[70vh] max-w-full rounded-[var(--r-md)] object-contain"
      />
    );
  }

  return (
    <Modal open={open} onClose={onClose} title={title} className="w-[min(720px,calc(100vw-32px))]">
      {images.length > 1 ? (
        <div role="tablist" className="mb-3 flex gap-[var(--sp-2)]">
          {images.map((img) => (
            <button
              key={img.key}
              type="button"
              role="tab"
              aria-selected={img.key === active?.key}
              onClick={() => setActiveKey(img.key)}
              className={cn(
                'cursor-pointer rounded-[var(--r-pill)] border px-[var(--sp-3)] py-[var(--sp-1)] text-[12px] font-semibold',
                img.key === active?.key
                  ? 'border-[var(--sage-2)] bg-[var(--sage-0)] text-[var(--ink-0)]'
                  : 'border-[var(--cream-2)] bg-[var(--cream-1)] text-[var(--ink-2)]',
              )}
            >
              {img.label}
            </button>
          ))}
        </div>
      ) : null}
      {body}
    </Modal>
  );
}
